import React, { useState, useEffect, useCallback } from 'react';
import { Shield, Eye, Ban, CheckCircle2, Trash2, UserX, Loader2, Search, FileVideo, Image as ImageIcon, Radio, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { useToast } from '../hooks/use-toast';
import api from '../api/client';
import PostPreviewModal from './PostPreviewModal';

// Phase 34 — Admin Content Moderation panel.
// All actions go through /api/admin/moderation/* — every mutation requires
// a typed motivation (≥3 chars) enforced both client-side and server-side.

const STATUS_STYLE = {
  active:    'bg-emerald-500/15 text-emerald-300',
  scheduled: 'bg-blue-500/15 text-blue-300',
  live:      'bg-red-500/15 text-red-300',
  ended:     'bg-muted text-muted-foreground',
  blocked:   'bg-amber-500/15 gold-text',
  pending:   'bg-amber-500/15 gold-text',
  deleted:   'bg-red-500/15 text-red-300',
};

const FILTERS = [
  { key: 'all', label: 'Tutti' },
  { key: 'post', label: 'Post' },
  { key: 'live', label: 'Live' },
  { key: 'blocked', label: 'Bloccati' },
  { key: 'pending', label: 'Pending' },
  { key: 'orphan', label: 'Orfani' },
  { key: 'stale', label: 'Live stale' },
];

const Stat = ({ label, value, testId }) => (
  <div className="rounded-xl border border-border bg-card p-3" data-testid={testId}>
    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className="mt-1 text-xl font-semibold tabular-nums">{value}</p>
  </div>
);

const ModerationDialog = ({ open, onOpenChange, title, description, busy, onConfirm, confirmLabel = 'Conferma', destructive = false }) => {
  const [reason, setReason] = useState('');
  useEffect(() => { if (!open) setReason(''); }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="moderation-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <label className="text-xs uppercase tracking-wide text-muted-foreground">Motivazione (obbligatoria)</label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Spiega il motivo dell'azione (min. 3 caratteri)…"
            rows={3}
            data-testid="moderation-dialog-reason"
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button
            onClick={() => onConfirm(reason.trim())}
            disabled={busy || reason.trim().length < 3}
            className={destructive ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-black hover:bg-neutral-800 text-white'}
            data-testid="moderation-dialog-confirm"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ContentRow = ({ row, onAction, onPreview }) => {
  const KindIcon = row.kind === 'live' ? Radio : (row.media_type === 'image' ? ImageIcon : FileVideo);
  const isBlocked = row.status === 'blocked';
  const liveHref = row.kind === 'live' ? `/lives/${row.id}` : null;
  return (
    <li className="grid grid-cols-12 gap-3 border-b border-border px-4 py-3 text-sm hover:bg-card/40" data-testid={`moderation-row-${row.id}`}>
      <div className="col-span-5 flex items-start gap-2 min-w-0">
        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${row.kind === 'live' ? 'bg-red-500/15 text-red-300' : 'bg-blue-500/15 text-blue-300'}`}>
          <KindIcon className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <p className="truncate font-semibold">{row.title || '(senza titolo)'}</p>
          <p className="truncate text-[11px] text-muted-foreground">
            <code>{row.id.slice(0,8)}</code> · @{row.creator_username}
            {row.creator_user_number ? ` · #${row.creator_user_number}` : ''}
            {row.is_paid ? ` · ${row.price} Cr · ${row.purchases_count} acquisti` : ''}
            {row.media_missing ? <span className="ml-1 text-red-300">· media mancante</span> : ''}
            {row.is_stale ? <span className="ml-1 gold-text">· stale</span> : ''}
          </p>
        </div>
      </div>
      <div className="col-span-2 self-center">
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${STATUS_STYLE[row.status] || 'bg-muted'}`}>
          {row.status}
        </span>
      </div>
      <div className="col-span-2 self-center text-[11px] text-muted-foreground tabular-nums">
        {new Date(row.created_at).toLocaleDateString()}
      </div>
      <div className="col-span-3 flex items-center justify-end gap-1">
        {liveHref ? (
          // Live streams still open in a new tab — no admin preview modal
          // for realtime rooms (would require joining the LiveKit session).
          <a
            href={liveHref}
            target="_blank"
            rel="noreferrer"
            className="rounded-md border border-border px-2 py-1 text-[11px] hover:bg-card"
            data-testid={`mod-view-${row.id}`}
            title="Apri live"
          >
            <Eye className="inline h-3 w-3" />
          </a>
        ) : (
          // Phase 51 · Post preview inside a modal — never navigates away
          // so the moderator keeps their current filters + list scroll.
          <button
            type="button"
            onClick={() => onPreview?.(row)}
            className="rounded-md border border-border px-2 py-1 text-[11px] hover:bg-card"
            data-testid={`mod-view-${row.id}`}
            title="Anteprima"
          >
            <Eye className="inline h-3 w-3" />
          </button>
        )}
        {!isBlocked ? (
          <button onClick={() => onAction('block', row)} className="rounded-md border border-border px-2 py-1 text-[11px] gold-text hover:bg-card" data-testid={`mod-block-${row.id}`} title="Blocca">
            <Ban className="inline h-3 w-3" />
          </button>
        ) : (
          <button onClick={() => onAction('unblock', row)} className="rounded-md border border-border px-2 py-1 text-[11px] text-emerald-300 hover:bg-card" data-testid={`mod-unblock-${row.id}`} title="Sblocca">
            <CheckCircle2 className="inline h-3 w-3" />
          </button>
        )}
        <button onClick={() => onAction('delete', row)} className="rounded-md border border-border px-2 py-1 text-[11px] text-red-300 hover:bg-card" data-testid={`mod-delete-${row.id}`} title="Elimina">
          <Trash2 className="inline h-3 w-3" />
        </button>
        <button onClick={() => onAction('block_user', row)} className="rounded-md border border-border px-2 py-1 text-[11px] text-red-400 hover:bg-card" data-testid={`mod-blockuser-${row.id}`} title="Blocca utente">
          <UserX className="inline h-3 w-3" />
        </button>
      </div>
    </li>
  );
};

const ContentModerationPanel = () => {
  const { toast } = useToast();
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [dialog, setDialog] = useState(null); // {action, row}
  const [busy, setBusy] = useState(false);
  // Phase 51 · Post preview modal state. Keeping this local (not URL-bound)
  // is intentional: closing it must NEVER reset the current search / filters
  // / scroll position of the moderation list.
  const [previewId, setPreviewId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (q.trim()) params.q = q.trim();
      if (filter === 'post') params.kind = 'post';
      else if (filter === 'live') params.kind = 'live';
      else if (filter === 'blocked') params.status = 'blocked';
      else if (filter === 'pending') params.status = 'pending';
      else if (filter === 'orphan') params.orphan = true;
      else if (filter === 'stale') { params.kind = 'live'; params.stale = true; }
      const [c, s] = await Promise.all([
        api.get('/admin/moderation/content', { params }),
        api.get('/admin/moderation/stats'),
      ]);
      setRows(c.data || []);
      setStats(s.data || null);
    } catch (e) {
      toast({ title: 'Errore caricamento moderation', description: e.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [filter, q, toast]);

  useEffect(() => { load(); }, [load]);

  const execAction = async (reason) => {
    if (!dialog) return;
    const { action, row } = dialog;
    setBusy(true);
    try {
      if (action === 'block') {
        await api.post(`/admin/moderation/${row.kind}s/${row.id}/block`, { reason });
      } else if (action === 'unblock') {
        await api.post(`/admin/moderation/${row.kind}s/${row.id}/unblock`, { reason });
      } else if (action === 'delete') {
        await api.delete(`/admin/moderation/${row.kind}s/${row.id}`, { params: { reason } });
      } else if (action === 'block_user') {
        await api.post(`/admin/moderation/users/${row.creator_id}/block`, { reason });
      }
      toast({ title: 'Azione eseguita', description: `${action} su ${row.kind} ${row.id.slice(0,8)} (creator notificato via email)` });
      setDialog(null);
      await load();
    } catch (e) {
      toast({ title: 'Azione fallita', description: e.response?.data?.detail?.split('|')[1] || e.response?.data?.detail || 'Riprova', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const dialogProps = (() => {
    if (!dialog) return null;
    const { action, row } = dialog;
    const labels = {
      block:      { title: `Blocca ${row.kind}`, desc: `"${row.title || row.id}" verrà nascosto e il creator notificato.`, confirm: 'Blocca', destructive: false },
      unblock:    { title: `Sblocca ${row.kind}`, desc: `"${row.title || row.id}" tornerà visibile.`, confirm: 'Sblocca', destructive: false },
      delete:     { title: `Elimina ${row.kind}`, desc: `${row.kind} eliminato definitivamente. Azione non reversibile.`, confirm: 'Elimina', destructive: true },
      block_user: { title: `Blocca utente @${row.creator_username}`, desc: `L'account verrà sospeso (status=paused).`, confirm: 'Sospendi account', destructive: true },
    };
    return labels[action];
  })();

  return (
    <section className="space-y-4" data-testid="content-moderation-panel">
      <div className="flex items-center gap-2">
        <Shield className="h-5 w-5 gold-text" />
        <h2 className="text-lg font-semibold">Content Moderation</h2>
      </div>

      {stats && (
        <div className="grid grid-cols-3 gap-2 md:grid-cols-5 lg:grid-cols-9">
          <Stat label="Totali"      value={stats.content_total}   testId="mod-stat-total" />
          <Stat label="Post"        value={stats.posts_total}     testId="mod-stat-posts" />
          <Stat label="Live"        value={stats.lives_total}     testId="mod-stat-lives" />
          <Stat label="Attivi"      value={stats.content_active}  testId="mod-stat-active" />
          <Stat label="Bloccati"    value={stats.content_blocked} testId="mod-stat-blocked" />
          <Stat label="Eliminati"   value={stats.content_deleted} testId="mod-stat-deleted" />
          <Stat label="User bloccati" value={stats.blocked_users} testId="mod-stat-blocked-users" />
          <Stat label="Media miss"  value={stats.media_missing}   testId="mod-stat-media-missing" />
          <Stat label="Live stale"  value={stats.lives_stale}     testId="mod-stat-lives-stale" />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Cerca per ID, username, email o #user_number…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()}
            className="pl-8 h-9 text-sm bg-background"
            data-testid="mod-search-input"
          />
        </div>
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`rounded-full px-3 py-1 text-xs font-semibold ${filter === f.key ? 'gold-gradient text-black' : 'border border-border text-muted-foreground hover:border-amber-500/40'}`}
            data-testid={`mod-filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-border bg-card">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            <AlertTriangle className="mx-auto mb-2 h-6 w-6" /> Nessun contenuto per questi filtri.
          </div>
        ) : (
          <ul className="divide-y divide-border" data-testid="mod-list">
            {rows.map((r) => (
              <ContentRow
                key={`${r.kind}:${r.id}`}
                row={r}
                onAction={(action, row) => setDialog({ action, row })}
                onPreview={(row) => setPreviewId(row.id)}
              />
            ))}
          </ul>
        )}
      </div>

      {/* Phase 51 · Preview modal (posts only; live streams open in-tab) */}
      <PostPreviewModal
        open={!!previewId}
        onOpenChange={(o) => !o && setPreviewId(null)}
        postId={previewId}
      />

      {dialog && dialogProps && (
        <ModerationDialog
          open
          onOpenChange={(o) => !o && setDialog(null)}
          title={dialogProps.title}
          description={dialogProps.desc}
          busy={busy}
          onConfirm={execAction}
          confirmLabel={dialogProps.confirm}
          destructive={dialogProps.destructive}
        />
      )}
    </section>
  );
};

export default ContentModerationPanel;
